import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import type { RequiredEventForm } from '../../types';
import getUserAllForms, { mapAllFormsItemToRequiredEventForm } from '../../hooks/user/getUserAllForms';
import { getFormRowKey } from '../../utils/formUtils';
import {
  ChildHeaderRow,
  EventFormActionButton,
  EventHeaderRow,
  StatusChip,
  TaskStatus,
} from './ToDoTableComponents';
import FormModal from './FormModal';

interface EventFormsSectionProps {
  /**
   * Called when the event-forms count changes (for badge). Pass null on load error.
   */
  onCountChange?: (count: number | null) => void;
}

/** Group forms by child (name or id), then by event name. API returns only incomplete forms. */
function groupFormsByChildAndEvent(forms: RequiredEventForm[]) {
  const byChild = new Map<string, { childLabel: string; forms: RequiredEventForm[] }>();

  for (const form of forms) {
    const childKey = form.childId != null ? String(form.childId) : (form.childName ?? '—');
    const childLabel = form.childName ?? '—';
    const existing = byChild.get(childKey);
    if (existing) {
      existing.forms.push(form);
    } else {
      byChild.set(childKey, { childLabel, forms: [form] });
    }
  }

  const sorted = Array.from(byChild.entries()).sort((a, b) =>
    a[1].childLabel.localeCompare(b[1].childLabel)
  );

  return sorted.map(([childKey, { childLabel, forms: childForms }]) => {
    const byEvent = new Map<string, RequiredEventForm[]>();
    for (const f of childForms) {
      const eventName = f.eventName ?? '—';
      const list = byEvent.get(eventName) ?? [];
      list.push(f);
      byEvent.set(eventName, list);
    }
    const eventEntries = Array.from(byEvent.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );
    return { childKey, childLabel, eventEntries };
  });
}

export function EventFormsSection({ onCountChange }: EventFormsSectionProps) {
  const [forms, setForms] = useState<RequiredEventForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formModalForm, setFormModalForm] = useState<RequiredEventForm | null>(null);
  const [formModalReadOnly, setFormModalReadOnly] = useState(false);

  const applyCount = useCallback(
    (response: { isSuccess: boolean; data?: { remainingCount?: number } } | null) => {
      if (!onCountChange) return;
      if (!response || !response.isSuccess) {
        onCountChange(null);
        return;
      }
      const count = response.data?.remainingCount ?? 0;
      onCountChange(count > 0 ? count : 0);
    },
    [onCountChange]
  );

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUserAllForms();
      applyCount(response);

      if (!response || !response.isSuccess || !response.data) {
        setForms([]);
        setError('We were unable to load your To Do items. Please try again later.');
        return;
      }

      const safeForms = Array.isArray(response.data.forms)
        ? response.data.forms.map(mapAllFormsItemToRequiredEventForm)
        : [];
      setForms(safeForms);
    } catch (err) {
      console.error('❌ Error loading forms:', err);
      setError('We were unable to load your To Do items. Please try again later.');
      setForms([]);
      applyCount(null);
    } finally {
      setLoading(false);
    }
  }, [applyCount]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleOpenForm = useCallback((form: RequiredEventForm) => {
    if (form.formUrl) {
      window.open(form.formUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (form.eventUuid || form.form) {
      setFormModalForm(form);
      setFormModalOpen(true);
      return;
    }
    console.warn('No formUrl, eventUuid, or inline form schema provided for form.', form);
  }, []);

  const openComplete = useCallback((form: RequiredEventForm) => {
    setFormModalReadOnly(false);
    handleOpenForm(form);
  }, [handleOpenForm]);

  const openView = useCallback((form: RequiredEventForm) => {
    setFormModalReadOnly(true);
    handleOpenForm(form);
  }, [handleOpenForm]);

  const handleFormSubmitted = useCallback(() => {
    fetchForms();
  }, [fetchForms]);

  const grouped = useMemo(() => groupFormsByChildAndEvent(forms), [forms]);

  const headerBox = (
    <Box
      sx={{
        px: 3,
        py: 2,
        bgcolor: '#f7fbff',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        Event Forms
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Complete required forms for each registered event.
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {headerBox}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {headerBox}
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={fetchForms} sx={{ textTransform: 'none' }}>
            Try again
          </Button>
        </Box>
      </Paper>
    );
  }

  if (forms.length === 0) {
    return (
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {headerBox}
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You&apos;re all caught up. No event forms to complete right now.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={3} sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {headerBox}
        <Box
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Form</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 140 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 160 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map(({ childKey, childLabel, eventEntries }) => (
                <Fragment key={childKey}>
                  <ChildHeaderRow name={childLabel} colSpan={4} />
                  {eventEntries.map(([eventName, eventForms]) => (
                    <Fragment key={`${childKey}-${eventName}`}>
                      <EventHeaderRow name={eventName} colSpan={4} />
                      {eventForms.map((form) => {
                        const status: TaskStatus = form.completed ? 'Complete' : 'Incomplete';
                        return (
                          <TableRow key={getFormRowKey(form)} hover>
                            <TableCell>{form.formName}</TableCell>
                            <TableCell>
                              <StatusChip status={status} />
                            </TableCell>
                            <TableCell>
                              {form.completed && form.completedAt
                                ? dayjs(form.completedAt).format('MM/DD/YYYY')
                                : form.completed
                                  ? 'Submitted'
                                  : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <EventFormActionButton
                                status={status}
                                onComplete={!form.completed ? () => openComplete(form) : undefined}
                                onView={form.completed ? () => openView(form) : undefined}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <FormModal
        open={formModalOpen}
        form={formModalForm}
        readOnly={formModalReadOnly}
        onClose={() => {
          setFormModalOpen(false);
          setFormModalForm(null);
          setFormModalReadOnly(false);
        }}
        onSubmitted={handleFormSubmitted}
      />
    </>
  );
}
